import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import './TransactionPage.css';
import './MiddleOfficePages.css';
import './LimitSetupPage.css';

// Mirrors the backend's getMiddleOfficeTier (utils/workflowStageAuth.js).
function getMiddleOfficeTier() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role === 'admin') return 'admin';
    const rank = { middle_office_user: 1, middle_office_officer: 2, middle_office_manager: 3 };
    let best = null;
    for (const a of user.assignments || []) {
      if (rank[a.role] && (!best || rank[a.role] > rank[best])) best = a.role;
    }
    return best;
  } catch {
    return null;
  }
}

// The limit fields carried in a request's payload, in display order.
const PAYLOAD_FIELDS = [
  { key: 'overall_exposure_limit', label: 'Overall Exposure Limit' },
  { key: 'currency_limit', label: 'Currency Limit' },
  { key: 'product_transaction_limit', label: 'Transaction Limit' },
  { key: 'product_money_market_limit', label: 'Money Market Limit' },
  { key: 'product_fx_limit', label: 'FX Limit' },
  { key: 'product_derivative_limit', label: 'Derivative Limit' },
  { key: 'product_repo_limit', label: 'Repo Limit' },
  { key: 'product_reverse_repo_limit', label: 'Reverse Repo Limit' },
  { key: 'product_gsec_limit', label: 'Gsec Limit' },
  { key: 'product_sell_and_buy_back_limit', label: 'Sell and Buy Back Limit' },
  { key: 'product_buy_and_sell_back_limit', label: 'Buy and Sell Back Limit' },
  { key: 'tenor_limit', label: 'Tenor Limit' },
  { key: 'settlement_risk_limit', label: 'Settlement Risk Limit' },
  { key: 'country_limit', label: 'Country Limit' },
  { key: 'group_limit', label: 'Group Limit' },
  { key: 'intraday_limit', label: 'Intraday Limit' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB');
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  return isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value);
};

export default function CounterpartyLimitRequestBlotter() {
  const [requests, setRequests] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [scopedToSelf, setScopedToSelf] = useState(false);

  const tier = useMemo(getMiddleOfficeTier, []);
  const isApprover = tier === 'admin' || tier === 'middle_office_officer' || tier === 'middle_office_manager';

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchRequests = async (status = statusFilter) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/limit-setup/requests`, {
        headers: getAuthHeaders(),
        params: status && status !== 'all' ? { status } : {}
      });
      setRequests(res.data?.data || []);
      setScopedToSelf(!!res.data?.scopedToSelf);
    } catch (err) {
      setRequests([]);
      setError(err?.response?.data?.error || err?.message || 'Could not load limit requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Counterparty names are resolved client-side so the blotter can show a
    // readable name rather than the stored id.
    axios.get(`${API_BASE_URL}/limit-setup/limit-counterparties`, { headers: getAuthHeaders() })
      .then((res) => setCounterparties(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCounterparties([]));
    fetchRequests('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    fetchRequests(value);
  };

  const counterpartyName = (req) => {
    const match = counterparties.find(
      (cp) => String(cp.id) === String(req.counterparty_id) && cp.type === req.counterparty_type
    );
    return match ? match.name : `#${req.counterparty_id}`;
  };

  const handleApprove = async (id) => {
    try {
      await axios.patch(`${API_BASE_URL}/limit-setup/${id}/approve`, {}, { headers: getAuthHeaders() });
      showToast('success', 'Request approved and the limit has been created.');
      fetchRequests();
    } catch (err) {
      showToast('danger', err?.response?.data?.error || 'Failed to approve request.');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/limit-setup/${id}/reject`,
        { reason: rejectReason },
        { headers: getAuthHeaders() }
      );
      showToast('success', 'Request rejected.');
      setRejectingId(null);
      setRejectReason('');
      fetchRequests();
    } catch (err) {
      showToast('danger', err?.response?.data?.error || 'Failed to reject request.');
    }
  };

  const statusBadge = (status) => {
    const cls = status === 'approved'
      ? 'mo-status mo-status--active'
      : status === 'rejected'
        ? 'mo-status'
        : 'mo-status mo-status--pending';
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    return <span className={cls}>{label}</span>;
  };

  const actionedBy = (req) => {
    if (req.status === 'approved') return `${req.approved_by_name || '-'} · ${formatDateTime(req.approved_at)}`;
    if (req.status === 'rejected') return `${req.rejected_by_name || '-'} · ${formatDateTime(req.rejected_at)}`;
    return '-';
  };

  return (
    <div className="limit-setup-page">
      <h2 className="fixed-income-header">Counterparty Limit Requests</h2>

      {toast && <div className={`alert alert-${toast.type}`}>{toast.message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {scopedToSelf && (
        <div className="alert alert-success">
          Showing your own requests. Officers and Managers see all submissions.
        </div>
      )}

      <div className="transaction-form-container">
        <div className="form-grid" style={{ maxWidth: 320 }}>
          <div className="form-group">
            <label className="form-label" htmlFor="statusFilter">Status</label>
            <select
              id="statusFilter"
              className="form-control"
              value={statusFilter}
              onChange={(e) => handleFilterChange(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {loading && <div>Loading requests...</div>}
        {!loading && requests.length === 0 && (
          <div>No limit requests{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.</div>
        )}

        {!loading && requests.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Req #</th>
                  <th>Counterparty</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Overall Exposure</th>
                  <th>Currency</th>
                  <th>Submitted By</th>
                  <th>Submitted At</th>
                  <th>Status</th>
                  <th>Actioned By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <React.Fragment key={req.id}>
                    <tr>
                      <td>{req.id}</td>
                      <td>{counterpartyName(req)}</td>
                      <td>{req.counterparty_type}</td>
                      <td style={{ textAlign: 'right' }}>{formatAmount(req.payload?.overall_exposure_limit)}</td>
                      <td>{req.payload?.currency_limit || req.payload?.currency || '-'}</td>
                      <td>{req.proposed_by_name || (req.proposed_by ? `user #${req.proposed_by}` : '-')}</td>
                      <td>{formatDateTime(req.proposed_at)}</td>
                      <td>{statusBadge(req.status)}</td>
                      <td>{actionedBy(req)}</td>
                      <td>
                        <div className="mo-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
                          >
                            {expandedId === req.id ? 'Hide' : 'View'}
                          </button>
                          {isApprover && req.status === 'pending' && (
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleApprove(req.id)}>
                              Approve
                            </button>
                          )}
                          {isApprover && req.status === 'pending' && rejectingId !== req.id && (
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => setRejectingId(req.id)}>
                              Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {rejectingId === req.id && (
                      <tr className="mo-pending-row">
                        <td colSpan={10}>
                          <div className="mo-pending-actions">
                            <input
                              className="form-control"
                              style={{ maxWidth: 320 }}
                              placeholder="Reason for rejection (optional)"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                            />
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => handleReject(req.id)}>
                              Confirm Reject
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => { setRejectingId(null); setRejectReason(''); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {expandedId === req.id && (
                      <tr className="mo-pending-row">
                        <td colSpan={10}>
                          <div className="limit-request-detail">
                            {PAYLOAD_FIELDS.map((f) => {
                              const raw = req.payload?.[f.key];
                              if (raw === undefined || raw === null || raw === '') return null;
                              return (
                                <div className="limit-request-detail-item" key={f.key}>
                                  <span className="limit-request-detail-label">{f.label}</span>
                                  <span className="limit-request-detail-value">
                                    {f.key === 'currency_limit' ? raw : formatAmount(raw)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          {req.status === 'rejected' && req.rejection_reason && (
                            <div style={{ marginTop: 8 }}>
                              <strong>Rejection reason:</strong> {req.rejection_reason}
                            </div>
                          )}
                          {req.status === 'approved' && req.created_limit_id && (
                            <div style={{ marginTop: 8 }}>
                              Created counterparty limit record <strong>#{req.created_limit_id}</strong>.
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="limit-setup-actions">
          <button type="button" className="btn btn-primary" onClick={() => fetchRequests()} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
    </div>
  );
}
